import type { Messages } from "./types";

export const en: Messages = {
	meta: {
		indexTitle: "GSplat Share — Share 3D data with a link",
		indexDescription: "Upload PLY, SPZ, SOG, or Super Splat HTML and create a share link that opens in the browser.",
		aboutTitle: "About - GSplat Share",
		aboutDescription: "GSplat Share turns Gaussian Splatting files into browser-ready share links.",
		viewerTitle: "GSplat - 3D Gaussian Splatting Viewer",
		viewerDescription: "Interactive 3D Gaussian Splatting viewer",
	},
	nav: {
		main: "Main",
		howItWorks: "How it works",
		formats: "Formats",
		about: "About",
		dashboard: "Links",
		login: "Sign in with Google",
		upload: "Upload",
	},
	footer: {
		tagline: "Turn 3D data into browser-ready links.",
		about: "About",
		terms: "Terms",
		privacy: "Privacy",
		contact: "Contact",
	},
	upload: {
		introTitle: "Your 3D capture is ready.\nNow share it.",
		introDescription:
			"Stop hunting for a way to share or setting up a viewer. Upload your Gaussian Splatting data and get a link that opens in the browser.",
		panelLabel: "01 — UPLOAD",
		studioLabel: "A simple place to share Gaussian Splatting data.",
		flowChoose: "Choose a file",
		flowPreview: "Check the view",
		flowShare: "Share the link",
		titleInvalid: "Enter a title of 1–100 characters.",
		descriptionLimit:
			"Keep the description within {{max}} characters. Starting view settings also use part of the saved description limit.",
		descriptionCount: "{{count}} / {{max}} characters",
		passwordInvalid: "Use 3–200 characters for the password.",
		workspaceTitle: "New share",
		workspaceDescription: "Choose a file, then set the title, expiration, and the view people see first.",
		inProgress: "Uploading",
		unsaved: "Unsaved changes",
		notUploaded: "Not uploaded yet",
		offlineHint: "You can review your file and settings here. Reconnect to start uploading.",
		selectionFailed: "Choose another file",
		sizeError: "The limit is 2 GB. Reduce the file size and choose it again.",
		emptyFile: "This file is empty. Choose a file that contains data.",
		changeFile: "Change file",
		removeFile: "Remove file",
		previewTitle: "Preview",
		previewHint: "Open the preview to check your file, or share it without a preview.",
		largePreviewHint: "Automatic preview is off for this large file. Opening it uses memory on your device.",
		linkHint: "Anyone with the link can view it. Set a password if you need to restrict access.",
		discardTitle: "Discard your changes?",
		discardDescription: "Your unsaved settings on this page will be lost.",
		discardTransfer: "This stops the upload and leaves the current workspace.",
		discardHint: "You may need to select the file again to continue.",
		keepEditing: "Keep working",
		discard: "Discard and continue",
		newFile: "Share another file",
		helpTitle: "How sharing works and supported formats",
		helpSteps:
			"1. Choose a file → 2. Set a title and expiry → 3. Copy the link after upload. You can also set the starting viewpoint in the preview.",
		helpFormats: "PLY, SPZ, SOG and HTML are supported, up to 2 GB.",
		helpAccount:
			"Sign in to find and edit your links in My shares. Save links yourself when creating them without signing in.",
		previewError: "This browser could not display the preview",
		previewErrorHint:
			"The file format, graphics support, or available memory may be the cause. Nothing has been sent to the server yet. Retry the preview or check the file in SuperSplat.",
		retryPreview: "Retry preview",
		title: "Upload a file",
		description: "PLY, SPZ, SOG, and Super Splat HTML are supported.",
		dropHere: "Drop your file here",
		orSelect: "or",
		selectFile: "Choose a file",
		hint: "Up to 2GB",
	},
	share: {
		instruction: "Copy the link and send it by email or chat.",
		saveHint:
			"If you created this link while signed out, copy and save it before closing this page. It will not appear in Manage links.",
		title: "Share link created",
		copy: "Copy",
		copied: "Copied",
		openViewer: "Open viewer →",
		manage: "Manage links",
	},
	about: {
		title: "About",
		subtitle: "A place to send Gaussian Splatting data in a form people can open immediately.",
		intro:
			"Upload PLY, SPZ, SOG, or Super Splat HTML and create a browser-ready share link. Links created after sign-in can be managed later.",
		featuresTitle: "Key features",
		features: [
			{ label: "Native formats", desc: "upload .ply, .spz, and .sog as-is" },
			{ label: "Super Splat HTML", desc: "— host viewers exported from the editor" },
			{ label: "Password protection", desc: "— optionally restrict viewing" },
			{ label: "Expiration", desc: "— auto-delete from 1 hour to unlimited" },
			{ label: "Browser viewer", desc: "— recipients view without installing anything" },
		],
		superSplatTitle: "Sharing from Super Splat",
		superSplatSteps: [
			"to edit your scene",
			"to export HTML",
			"Drag and drop into GSplat Share",
			"Share the generated link",
		] as [string, string, string, string],
		superSplatEditor: "Super Splat editor",
		creatorTitle: "Project maker",
		creatorBio: "Ryuto Haga — a personal project for making 3D technology easier to share.",
	},
	uploadModal: {
		errorUncertain: "We could not confirm completion on the server",
		recoveryUncertainSave:
			"Saving may have completed. If signed in, check your shared links before retrying. Resending may create a duplicate file.",
		recoveryUncertainLink:
			"The link may already exist. If signed in, check your shared links first. Retrying reuses the uploaded file, but could create a duplicate link.",
		stageWaiting: "Waiting for the server to confirm this transfer",
		errorOffline: "Your device appears to be offline",
		recoveryOffline:
			"Check your Wi-Fi or mobile connection, then try again. Your file and settings are still selected.",
		errorNetwork: "Could not connect to the server",
		recoveryNetwork:
			"The connection or server may be unavailable. Check whether other sites load, then wait a moment and try again.",
		errorTimeout: "The server took too long to respond",
		recoveryTimeout:
			"A slow connection or busy server may be the cause. Check your connection and retry. If it persists, try again later.",
		errorServer: "The server could not process your upload",
		recoveryServer:
			"The server returned an error. This does not mean your file is damaged. Wait a moment and try again.",
		errorBusy: "Too many requests right now",
		recoveryBusy: "Wait a little before trying again. Your file and settings are still selected.",
		errorFile: "Check your file and upload settings",
		recoveryFile:
			"Check that your file is PLY, SPZ, SOG or HTML, is not empty, and is no larger than 2 GB. Review the title and password, then try again.",
		errorPermission: "Upload permission could not be verified",
		recoveryPermission:
			"Check your sign-in status. If you need to sign in again, keep a note of your file and settings, then retry.",
		errorUnknown: "The upload could not be completed",
		recoveryUnknown:
			"We could not determine the cause. Your file and settings are still selected. Retry, or contact us if the problem continues.",
		stagePreparing: "Preparing your upload",
		stageSending: "Uploading your file",
		stageFinalizing: "Upload sent · saving your file",
		stageLink: "Creating your share link",
		optionalSettings: "Add a description or password (optional)",
		cancelTransfer: "Cancel upload",
		cancelled: "Upload cancelled.",
		retryHint: "Your file is still selected. Check your connection and try again.",
		progressNote: "Progress shows data sent by your browser. Saving and link creation follow the transfer.",
		title: "Create share link",
		subtitle: "Set presentation and viewing conditions, then create a browser-ready link.",
		showPreview: "Show preview",
		hidePreview: "Hide preview",
		expandPreview: "Expand preview",
		shrinkPreview: "Shrink preview",
		adjustInitialCamera: "Initial viewer view",
		adjustInitialCameraDesc:
			"Rotate, pan, and zoom the preview to choose the first view visitors see. The model orientation and coordinates are left unchanged.",
		cameraPoseLocked: "Initial view saved",
		cameraPoseUnlock: "Return to automatic view",
		setCameraPoseAsDefault: "Use current view as initial view",
		centerCalculationMethod: "Center calculation method",
		centerCalculationRobust: "Object center (Recommended)",
		centerCalculationOrigin: "Origin (0,0,0)",
		centerCalculationAabb: "Entire bounding box",
		max: "Max",
		overLimit: "Over size limit",
		settingsLegend: "Basic information",
		titleLabel: "Title",
		titlePlaceholder: "e.g. My room 3D scan",
		descriptionLabel: "Description",
		descriptionPlaceholder: "Brief description of the model",
		expirationLabel: "Expiration",
		securityLegend: "Viewing controls",
		passwordProtection: "Password protection",
		passwordProtectionDesc: "Require a password to view this model",
		passwordLabel: "Set password",
		passwordPlaceholder: "Password (3+ characters)",
		showPassword: "Show password",
		hidePassword: "Hide password",
		passwordGood: "Good",
		passwordTooShort: "Too short",
		passwordNote: "Viewers will need this password to open the model",
		errorCode: "Error code: {{code}}",
		uploading: "Uploading...",
		saving: "Saving...",
		sending: "Sending...",
		remainingHours: "{{hours}}h {{minutes}}m remaining",
		remainingMinutes: "{{minutes}}m {{seconds}}s remaining",
		remainingSeconds: "{{seconds}}s remaining",
		cancel: "Cancel",
		submit: "Create share link",
		processing: "Processing...",
		expiration: {
			hour: "1 hour (expires at {{time}})",
			day: "1 day (expires on {{date}})",
			week: "1 week (expires on {{date}})",
			unlimited: "Never expires",
		},
	},
	viewer: {
		fullscreenUnavailable: "Fullscreen is unavailable in this browser.",
		renderError: "Could not display this 3D file",
		touchHint: "Touch to explore · Footprints to walk",
		downloadManifest: "Download manifest",
		manifestHint: "This reference file is for streaming. It does not include the 3D data.",

		fullscreen: "Fullscreen",
		exitFullscreen: "Exit fullscreen",
		download: "Download",
		refresh: "Reload",
		htmlError: "Could not load this content",

		exploreHint: "Mouse & keys to explore · Footprints to walk",
		share: "Share link",
		linkCopied: "Copied",
		copyFallback: "Select and copy this link.",
		uploadOwn: "Share your own 3D capture",

		notFound: "File not found",
		notFoundWithError: "File not found: {{message}}",
		apiUnavailable: "Could not connect to the server",
		apiUnavailableHint: "Please try again in a moment. If it keeps failing, contact us.",
		passwordProtected: "This model is password protected.",
		passwordTitle: "Password protected",
		passwordDesc: "This model is password protected. Enter the password to view it.",
		passwordPlaceholder: "Enter password",
		passwordIncorrect: "Incorrect password. Please try again.",
		verifying: "Verifying...",
		showModel: "View model",
		loading: "Loading 3D model",
		close: "Close",
		details: "Details",
		format: "Format",
		size: "Size",
		views: "Views",
		expires: "Expires",
		promo: "Turn 3D data into a browser-ready link.",
		upload: "Upload",
	},
	shareButton: {
		copyUrl: "Copy URL",
		shareOnX: "Share on X",
		embed: "Embed",
		copied: "Copied to clipboard.",
		embedTitle: "Embed code",
		copyCode: "Copy code",
	},
	loadingOverlay: {
		title: "Uploading",
		subtitle: "Preparing your share link.",
	},
	notFound: {
		code: "404",
		title: "Page not found",
		description: "Please check the URL.",
		backHome: "Back to home",
	},
	error: {
		recovery: "This page could not be loaded. Reload it or return home and try again.",
		title: "An error occurred",
		reload: "Reload page",
		unknown: "Unknown error",
	},
	alerts: {
		fileTooLarge: "File size exceeds the maximum limit of {{max}}MB",
		fileTooLargeRetry: "The file is too large. Please try a smaller file or contact support for assistance.",
		fileTooLargeSimple: "The file is too large. Please upload a smaller file.",
		unsupportedFile: "Please select a supported file (.html, .ply, .spz, .sog).",
		shareSaveFailed: "The upload completed, but saving the share link failed. Please try again in a moment.",
		uploadFailed: "Upload failed. Please try again.",
		uploadFailedWithMessage: "Upload failed: {{message}}",
	},
	language: {
		switch: "Language",
		ja: "日本語",
		en: "English",
	},
};
